import { ref } from "vue";
import initGoogleMaps from "@/utilities/gMapsInit";
import cachingDecorator from "@/utilities/cachingDecorator";

const SESSION_EXPIRY_TIME_MS = 120000;

// TODO:
// WARNING: Google Maps JavaScript API should be loaded with loading=async for optimal performance.
// See https://goo.gle/js-api-loading for best-practice loading patterns.
// Ensure that your gMapsInit utility loads the API script with loading=async, e.g.:
// <script src="https://maps.googleapis.com/maps/api/js?key=YOUR_API_KEY&libraries=places&loading=async"></script>
// overrideMethod @ hook.js:608
// Uca @ js?key=AIzaSyDzHr3MiUS6KTd8Bm89Y6SIN5vQZeM67GI&libraries=places&callback=googleMapsCallback:1480

// TODO: Migrate from google.maps.places.AutocompleteService to google.maps.places.AutocompleteSuggestion before March 1st, 2025.
// See: https://developers.google.com/maps/documentation/javascript/places-migration-overview
// js?key=AIzaSyDzHr3MiUS6KTd8Bm89Y6SIN5vQZeM67GI&libraries=places&callback=googleMapsCallback:83 As of March 1st, 2025, google.maps.places.AutocompleteService is not available to new customers. Please use google.maps.places.AutocompleteSuggestion instead. At this time, google.maps.places.AutocompleteService is not scheduled to be discontinued, but google.maps.places.AutocompleteSuggestion is recommended over google.maps.places.AutocompleteService. While google.maps.places.AutocompleteService will continue to receive bug fixes for any major regressions, existing bugs in google.maps.places.AutocompleteService will not be addressed. At least 12 months notice will be given before support is discontinued. Please see https://developers.google.com/maps/legacy for additional details and https://developers.google.com/maps/documentation/javascript/places-migration-overview for the migration guide.
// (anonymous) @ js?key=AIzaSyDzHr3MiUS6KTd8Bm89Y6SIN5vQZeM67GI&libraries=places&callback=googleMapsCallback:1717
// (anonymous) @ js?key=AIzaSyDzHr3MiUS6KTd8Bm89Y6SIN5vQZeM67GI&libraries=places&callback=googleMapsCallback:1717Understand this warning
// js?key=AIzaSyDzHr3MiUS6KTd8Bm89Y6SIN5vQZeM67GI&libraries=places&callback=googleMapsCallback:83 As of March 1st, 2025, google.maps.places.AutocompleteService is not available to new customers. Please use google.maps.places.AutocompleteSuggestion instead. At this time, google.maps.places.AutocompleteService is not scheduled to be discontinued, but google.maps.places.AutocompleteSuggestion is recommended over google.maps.places.AutocompleteService. While google.maps.places.AutocompleteService will continue to receive bug fixes for any major regressions, existing bugs in google.maps.places.AutocompleteService will not be addressed. At least 12 months notice will be given before support is discontinued. Please see https://developers.google.com/maps/legacy for additional details and https://developers.google.com/maps/documentation/javascript/places-migration-overview for the migration guide.

interface SessionToken {
  token: google.maps.places.AutocompleteSessionToken | undefined;
  startTime: number | null;
}

const useGmapsPlacesService = () => {
  const sessionToken = ref<SessionToken>({
    token: undefined,
    startTime: null,
  });

  const geocoder = ref<google.maps.Geocoder | null>(null);
  const autocompleteService =
    ref<google.maps.places.AutocompleteService | null>(null);
  const placesService = ref<google.maps.places.PlacesService | null>(null);

  const findAddressComponent = (
    addressComponents: Array<google.maps.GeocoderAddressComponent>,
    types: string | string[],
  ) => {
    return addressComponents.find((component) =>
      Array.isArray(types)
        ? component.types.some((type) => types.includes(type))
        : component.types.includes(types),
    );
  };

  const getAddressComponents = (
    addressComponents?: Array<google.maps.GeocoderAddressComponent>,
  ) => {
    addressComponents = addressComponents || [];
    const streetNumberComponent = findAddressComponent(
      addressComponents,
      "street_number",
    );
    const routeComponent = findAddressComponent(addressComponents, "route");
    const postalCodeComponent = findAddressComponent(
      addressComponents,
      "postal_code",
    );
    const postalTownComponent = findAddressComponent(addressComponents, [
      "postal_town",
      "locality",
    ]);
    const administrativeAreaLevel1Component = findAddressComponent(
      addressComponents,
      ["administrative_area_level_1", "political"],
    );

    return {
      streetNumber: streetNumberComponent?.short_name || "",
      route: routeComponent?.short_name || "",
      postalCode: postalCodeComponent?.short_name || "",
      city: postalTownComponent?.short_name || "",
      county: administrativeAreaLevel1Component?.short_name || "",
    };
  };

  const getAutocompletePredictions = async (input: string) => {
    const trimmedInput = input.trim();
    if (!trimmedInput) {
      return { input: trimmedInput, results: [] };
    }

    const gmaps = await initGoogleMaps();
    if (!autocompleteService.value) {
      autocompleteService.value = new gmaps.maps.places.AutocompleteService();
    }

    const response = await autocompleteService.value.getPlacePredictions({
      input: trimmedInput,
      componentRestrictions: { country: ["no"] },
      types: ["street_address", "route"],
      sessionToken: await getSessionToken(),
    });

    return { input: trimmedInput, results: response.predictions.slice() };
  };

  const getPlaceDetails = async (placeId: string) => {
    const gmaps = await initGoogleMaps();
    return new Promise<google.maps.places.PlaceResult | null>((resolve) => {
      if (!placesService.value) {
        placesService.value = new gmaps.maps.places.PlacesService(
          document.createElement("div"),
        );
      }
      placesService.value.getDetails(
        {
          placeId: placeId,
          sessionToken: getAndResetSessionToken(),
          fields: ["place_id", "geometry", "address_component"],
        },
        (response) => resolve(response),
      );
    });
  };

  const getSessionToken = async () => {
    const startTime = sessionToken.value.startTime;
    if (
      !sessionToken.value.token ||
      !startTime ||
      new Date().getTime() - startTime > SESSION_EXPIRY_TIME_MS
    ) {
      const gmaps = await initGoogleMaps();
      sessionToken.value.token =
        new gmaps.maps.places.AutocompleteSessionToken();
      sessionToken.value.startTime = new Date().getTime();
    }
    return sessionToken.value.token;
  };

  const getAndResetSessionToken = () => {
    const token = sessionToken.value.token;
    sessionToken.value.token = undefined;
    return token;
  };

  const checkIfAutocompletePredictionIsStreetAddress = (
    prediction: google.maps.places.AutocompletePrediction,
  ) => {
    return prediction.types.includes("street_address");
  };

  const getAutocompletePredictionAddressText = (
    prediction: google.maps.places.AutocompletePrediction,
  ) => {
    return prediction.description.replace(", Norway", "");
  };

  const reverseGeocode = async (request: {
    location?: { lat: number; lng: number } | null;
    placeId?: string | null;
  }) => {
    if (!geocoder.value) {
      const gmaps = await initGoogleMaps();
      geocoder.value = new gmaps.maps.Geocoder();
    }
    const response = await geocoder.value.geocode(request);
    return response.results[0];
  };

  const cachedReverseGeocode = cachingDecorator(reverseGeocode);

  return {
    getSessionToken,
    getPlaceDetails,
    getAddressComponents,
    cachedReverseGeocode,
    findAddressComponent,
    getAndResetSessionToken,
    getAutocompletePredictions,
    getAutocompletePredictionAddressText,
    checkIfAutocompletePredictionIsStreetAddress,
  };
};

export default useGmapsPlacesService;
